/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { DataMap, HeadersVisibility } from '@grapecity/wijmo.grid';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox } from 'components/selects/ComboBox';
import { CommonUtil } from 'utils/CommonUtil';
import CustomChip from 'components/selects/CustomChip';
import { Code, commonYNList } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { findActivities, findOrgEqclGroups, saveActivities } from 'apis/setup/SetupActivity';
import { ActivityCondition, EqclGroup, SpActivity } from 'models/setup/SetupActivity';
import { SetupStep } from 'models/setup/SetupStep';
import { findSetupStep } from 'apis/setup/SetupStep';
import { IconButton } from 'components/buttons/IconSVG';
import { CrudCode } from 'models/common/Edit';
import { CommonYN } from 'models/common/Common';
import { hasRole } from 'utils/SessionUtil';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 셋업 단계별 Activity 등록/조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.20   jshwang12       신규 생성
 * 2026.09.01   jshwang12       권한 설정  (FA 관리자, 시스템관리자)
 */
const SetupActivityPage = () => {
  const gridRef = useRef<any>();
  const selectedOrgTpCd = useRef<string>();
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const defaultCondition = {
    orgTpCd: 'FA',
    istlDivsCd: '',
    prjSpCd: '',
  };
  const [condition, setCondition] = useState<ActivityCondition>({ ...defaultCondition });
  const [errors, setErrors] = useState<any>({});
  const [rowData, setRowData] = useState<any[]>([]);
  const [prjSpCds, setPrjSpCds] = useState<SetupStep[]>([]);
  const [code, setCode] = useState<any>();
  const [selectedEqclIds, setSelectedEqclIds] = useState<string[]>(['ALL']);
  const [eqclGroups, setEqclGroups] = useState<EqclGroup[]>([]);
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const optionsEqclGroups = useMemo(() => {
    return [
      { cmnCd: 'ALL', cmnCdNm: String(t('com.label.전체', '전체')) },
      ...Array.from(
        new Map<string, Code>(
          (eqclGroups || []).map((item) => [
            item.eqclId,
            { cmnCd: item.eqclId, cmnCdNm: item.eqclId },
          ])
        ).values()
      ),
    ];
  }, [eqclGroups]);

  const optionPrjSpCds = useMemo(() => {
    return (prjSpCds || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.prjSpCd,
          cmnCdNm: cur.prjSpNm,
        },
      ],
      [] as Code[]
    );
  }, [prjSpCds]);

  useEffect(() => {
    getCommonCodes();
    // [26.09.01] FA 관리자, 시스템관리자만 수정 가능
    if (hasRole('ADM') || hasRole('FA_MANAGER')) {
      setHasAuth(true);
    }
    handleSearch(condition);
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('PRJ_ORG_TP_CD'),
      getCommonCodeNames('PRJ_SP_DIVS_CD'), // 설치구분 (ex. 운송, 기구 등)
      getCommonCodeNames('ISSU_CRTN_CD'), // 발행기준
      getCommonCodeNames('PSG_CRGE_TP_CD'), // 처리담당자유형
      findSetupStep({ useYn: 'Y' }),
    ]).then(([prjOrgTpCd, prjSpDivsCd, issuCrtnCd, psgCrgeTpCd, prjSpCd]) => {
      setCode({
        prjOrgTpCd: prjOrgTpCd,
        prjSpDivsCd: prjSpDivsCd,
        issuCrtnCd: issuCrtnCd,
        psgCrgeTpCd: psgCrgeTpCd,
      });
      setPrjSpCds(prjSpCd);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(condition);
    }
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRow = useEvent(() => {
    if (!selectedOrgTpCd.current) {
      openMessageBar({
        type: 'warning',
        content: String(t('setup.label.조회 후 등록해 주세요.', '조회 후 등록해 주세요.')),
      });
      return;
    }
    if ((eqclGroups || []).length < 1) {
      openMessageBar({
        type: 'warning',
        content: String(t('setup.label.등록된 설비군이 없습니다.', '등록된 설비군이 없습니다.')),
      });
      return;
    }
    const newRow = {
      crudKey: CrudCode.CREATE,
      spActvId: '',
      orgTpCd: selectedOrgTpCd.current,
      istlDivsCd: '',
      prjSpCd: '',
      actvNm: '',
      issuCrtnCd: '',
      issuPontVal: null,
      psgCrgeTpCd: '',
      rltMndYn: CommonYN.N,
      sortOrd: null,
      useYn: CommonYN.Y,
      eqcls: [],
    } as SpActivity;
    const rows = [newRow, ...rowData];
    setRowData(rows);
  });

  /**
   * 행삭제
   */
  const handleDelRow = () => {
    const selectedRows = gridRef.current?.rows?.filter((r) => r.isSelected);
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.셋업 단계 별 Activity 목록', '셋업 단계 별 Activity 목록'),
      t('setup.label.셋업 단계 별 Activity 목록', '셋업 단계 별 Activity 목록')
    );
  };

  const handleSave = () => {
    if (!(rowData || []).filter((o) => o.crudKey).length) {
      return;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        saveActivities(rowData).then((res) => {
          openMessageBar({
            type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
            content:
              res?.successOrNot === 'Y'
                ? t('com.label.저장되었습니다.', '저장되었습니다.')
                : res?.data && typeof res?.data === 'string'
                ? res.data
                : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
          if (res?.successOrNot === 'Y') {
            handleSearch(condition);
          }
        });
      },
    });
  };

  const handleSearch = (params) => {
    setErrors({
      orgTpCd: !params?.orgTpCd,
    });
    selectedOrgTpCd.current = params?.orgTpCd;
    if (params?.orgTpCd) {
      Promise.all([findOrgEqclGroups(params?.orgTpCd), findActivities(params)]).then(
        ([eqclGroups, rows]) => {
          setEqclGroups(eqclGroups);
          setRowData(rows);
        }
      );
    } else {
      setEqclGroups([]);
      setRowData([]);
    }
  };

  const layoutDefinition = useMemo(() => {
    const mapPrjSpCd = new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm');
    mapPrjSpCd.getDisplayValues = (item) => {
      // 선택한 셋업구분값에 따라 셋업단계 콤보박스 필터링 (공통단계 제외)
      return (prjSpCds || [])
        .filter((o) => {
          return o.istlDivsCd === item.istlDivsCd;
        })
        .map((o) => o.prjSpNm || '');
    };
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        align: 'center',
        isReadOnly: true,
        visible: !hasAuth,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        visible: hasAuth,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        header: String(t('setup.label.설비군', '설비군')),
        columns: (eqclGroups || [])
          .filter(
            (o) =>
              (selectedEqclIds || []).includes(o.eqclId) || (selectedEqclIds || []).includes('ALL')
          )
          .reduce(
            (acc, cur) => [
              ...acc,
              {
                binding: `eqclId_${cur.eqclId}`,
                header: cur.eqclId,
                minWidth: 43,
                width: 50,
                align: 'center',
                isReadOnly: true,
                cssClass: 'WijmoCheck',
                cellTemplate: (params) => {
                  const checked = (params.item?.eqcls || []).includes(cur.eqclId) ? 'checked' : '';
                  const disabled = !hasAuth ? 'disabled' : '';
                  return `<div class="checkbox">
                          <input type="checkbox" id="eqclId" value="${cur.eqclId}" ${checked} ${disabled} />
                          <label /> 
                        </div>
                  `;
                },
              },
            ],
            [] as any[]
          ),
      },
      {
        binding: 'istlDivsCd',
        header: String(t('setup.label.구분', '구분')),
        align: 'center',
        minWidth: 50,
        width: 82,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prjSpDivsCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'prjSpCd',
        header: String(t('setup.label.셋업 단계', '셋업 단계')),
        align: 'left',
        minWidth: 40,
        width: '*',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: mapPrjSpCd,
      },
      {
        binding: 'actvNm',
        header: String(t('setup.label.Activity 명', 'Activity 명')),
        align: 'left',
        minWidth: 40,
        width: '*',
      },
      {
        binding: 'issuCrtnCd',
        header: String(t('setup.label.발행기준', '발행기준')),
        align: 'center',
        minWidth: 80,
        width: 100,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.issuCrtnCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'issuPontVal',
        header: String(t('setup.label.D일', 'D일')),
        align: 'center',
        minWidth: 50,
        width: 50,
        isRequired: false,
      },
      {
        binding: 'psgCrgeTpCd',
        header: String(t('setup.label.처리 담당자', '처리 담당자')),
        align: 'center',
        minWidth: 80,
        width: 150,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.psgCrgeTpCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'rltMndYn',
        header: String(t('setup.label.결과필수', '결과필수')),
        align: 'center',
        minWidth: 50,
        width: 82,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(commonYNList || [], 'cmnCd', 'cmnCd'),
      },
    ];
  }, [hasAuth, eqclGroups, selectedEqclIds]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.headersVisibility = hasAuth
        ? HeadersVisibility.All
        : HeadersVisibility.Column;
    }
  }, [hasAuth]);

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
    grid.headersVisibility = hasAuth ? HeadersVisibility.All : HeadersVisibility.Column;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (e.target instanceof HTMLInputElement) {
        if (e.target.id === 'eqclId') {
          const arr = [...(item.eqcls || [])];
          if (e.target.checked) {
            if (!arr.includes(e.target.value)) {
              arr.push(e.target.value);
            }
          } else {
            const index = arr.indexOf(e.target.value);
            if (index !== -1) {
              arr.splice(index, 1);
            }
          }
          item.eqcls = arr;
          if (item.crudKey !== CrudCode.CREATE) {
            item.crudKey = CrudCode.UPDATE;
          }
          gridRef.current?.refresh();
        }
      }
    });
  });

  return (
    <>
      <style>
        {`
        /* 체크박스 disable css */ 
        .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.WijmoCheck .checkbox input[type=checkbox]:disabled+label {
          opacity: 1;
        }
        .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.WijmoCheck .checkbox input[type=checkbox]:disabled {
          cursor: default;
        }
        `}
      </style>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>
                  <span className="dot">{String(t('setup.label.조직유형', '조직유형'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={code?.prjOrgTpCd || []}
                  defaultValue={condition?.orgTpCd}
                  onChange={(value) => {
                    handleOnChange('orgTpCd', value);
                    setErrors((prev) => ({
                      ...prev,
                      orgTpCd: false,
                    }));
                  }}
                  isError={errors?.orgTpCd}
                  msgError={String(
                    t('setup.label.조직유형을 선택해 주세요.', '조직유형을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('setup.label.셋업구분', '셋업구분'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={code?.prjSpDivsCd || []}
                  defaultValue={condition?.istlDivsCd}
                  onChange={(value) => handleOnChange('istlDivsCd', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('setup.label.셋업단계', '셋업단계'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionPrjSpCds || []}
                  defaultValue={condition?.prjSpCd}
                  onChange={(value) => handleOnChange('prjSpCd', value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={defaultCondition}
            setReset={setCondition}
            onSearch={() => handleSearch(condition)}
          />
        </SearchBoxRow>
      </SearchBox>

      {(optionsEqclGroups || []).length > 1 && (
        <SubTitleLayout>
          <ControlBtnGroup>
            <CustomChip
              options={optionsEqclGroups || []}
              defaultValue={selectedEqclIds || []}
              singleSelect={false}
              onChange={(value) => {
                let values = (value || []) as string[];
                if (values.includes('ALL')) {
                  if (selectedEqclIds.includes('ALL') && values.length > 1) {
                    values = values.filter((v) => v !== 'ALL');
                  } else {
                    values = ['ALL'];
                  }
                }
                if (values.length < 1) {
                  values = [...selectedEqclIds];
                }
                setSelectedEqclIds(values);
              }}
            />
          </ControlBtnGroup>
        </SubTitleLayout>
      )}

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.셋업 단계 별 Activity 목록', '셋업 단계 별 Activity 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isReadOnly={!hasAuth}
        height={(optionsEqclGroups || []).length > 1 ? 504 : 581}
        initialized={onInitialized}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={() => handleSave()}>
            {t('com.label.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default SetupActivityPage;
